import { useState, useEffect, useCallback, useRef } from 'react';
import { analyticsService } from '../services/analytics';
import {
  AnalyticsDateRange,
  UsersAnalytics,
  BookingsAnalytics,
  PaymentsAnalytics,
  ContentAnalytics,
  GroupSessionsAnalytics,
  SupportAnalytics,
} from '../types/analytics';

// ============================================================================
// Generic analytics fetch hook
// ============================================================================

interface UseAnalyticsState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function useAnalyticsEndpoint<T>(
  fetcher: (params?: AnalyticsDateRange) => Promise<T>,
  dateRange?: AnalyticsDateRange
) {
  const [state, setState] = useState<UseAnalyticsState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetcherRef.current(dateRange);
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch analytics';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// Individual analytics hooks
// ============================================================================

export function useUsersAnalytics(dateRange?: AnalyticsDateRange) {
  return useAnalyticsEndpoint<UsersAnalytics>(
    analyticsService.getUsersAnalytics,
    dateRange
  );
}

export function useBookingsAnalytics(dateRange?: AnalyticsDateRange) {
  return useAnalyticsEndpoint<BookingsAnalytics>(
    analyticsService.getBookingsAnalytics,
    dateRange
  );
}

export function usePaymentsAnalytics(dateRange?: AnalyticsDateRange) {
  return useAnalyticsEndpoint<PaymentsAnalytics>(
    analyticsService.getPaymentsAnalytics,
    dateRange
  );
}

export function useContentAnalytics(dateRange?: AnalyticsDateRange) {
  return useAnalyticsEndpoint<ContentAnalytics>(
    analyticsService.getContentAnalytics,
    dateRange
  );
}

export function useGroupSessionsAnalytics(dateRange?: AnalyticsDateRange) {
  return useAnalyticsEndpoint<GroupSessionsAnalytics>(
    analyticsService.getGroupSessionsAnalytics,
    dateRange
  );
}

export function useSupportAnalytics(dateRange?: AnalyticsDateRange) {
  return useAnalyticsEndpoint<SupportAnalytics>(
    analyticsService.getSupportAnalytics,
    dateRange
  );
}

// ============================================================================
// Combined dashboard hook — fetches all 6 in parallel
// ============================================================================

export function useDashboardAnalytics(dateRange?: AnalyticsDateRange) {
  const users = useUsersAnalytics(dateRange);
  const bookings = useBookingsAnalytics(dateRange);
  const payments = usePaymentsAnalytics(dateRange);
  const content = useContentAnalytics(dateRange);
  const groupSessions = useGroupSessionsAnalytics(dateRange);
  const support = useSupportAnalytics(dateRange);

  const loading =
    users.loading ||
    bookings.loading ||
    payments.loading ||
    content.loading ||
    groupSessions.loading ||
    support.loading;

  const refetchAll = useCallback(() => {
    users.refetch();
    bookings.refetch();
    payments.refetch();
    content.refetch();
    groupSessions.refetch();
    support.refetch();
  }, [users.refetch, bookings.refetch, payments.refetch, content.refetch, groupSessions.refetch, support.refetch]);

  return {
    users,
    bookings,
    payments,
    content,
    groupSessions,
    support,
    loading,
    refetchAll,
  };
}
